import { useState, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export const useMeetingCapture = () => {
  const { toast } = useToast();
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          sampleRate: 24000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });
      
      const mediaRecorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      chunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.start(1000); // Collect data every second
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
      setRecordingDuration(0);

      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);

      toast({
        title: "Recording started",
        description: "Speak naturally about the meeting",
      });
    } catch (error) {
      console.error('Error starting recording:', error);
      toast({
        title: "Microphone access denied",
        description: "Please allow microphone access to record meetings",
        variant: "destructive",
      });
    }
  }, [toast]);

  const stopRecording = useCallback((): Promise<Blob> => {
    return new Promise((resolve) => {
      if (!mediaRecorderRef.current) {
        resolve(new Blob());
        return;
      }

      const mediaRecorder = mediaRecorderRef.current;
      
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/webm' });
        
        // Stop all tracks
        mediaRecorder.stream.getTracks().forEach(track => track.stop());
        
        // Clear timer
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        
        setIsRecording(false);
        resolve(audioBlob);
      };
      
      mediaRecorder.stop();
    });
  }, []);

  const processMeeting = useCallback(async (audioBlob: Blob, contextJobId?: string) => {
    setIsProcessing(true);
    
    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('User not authenticated');
      }

      // Convert blob to base64
      const reader = new FileReader();
      const base64Promise = new Promise<string>((resolve, reject) => {
        reader.onloadend = () => {
          const base64 = (reader.result as string).split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
      });
      
      reader.readAsDataURL(audioBlob);
      const base64Audio = await base64Promise;

      console.log('Calling process-meeting function...');

      // Call edge function
      console.log('Calling with user ID:', user.id);
      const { data, error } = await supabase.functions.invoke('process-meeting', {
        body: {
          audio: base64Audio,
          contextJobId,
          userId: user.id
        }
      });

      console.log('Edge function response:', { data, error });

      if (error) {
        console.error('Edge function error:', error);
        const errorMsg = error.message || JSON.stringify(error) || 'Failed to process meeting audio';
        throw new Error(errorMsg);
      }

      if (!data) {
        throw new Error('No data returned from meeting processing');
      }

      if (data.error) {
        console.error('Meeting processing error:', data.error);
        throw new Error(data.error);
      }

      console.log('Meeting parsed:', data);
      console.log('Meeting object:', data.meeting);
      console.log('Proposed tasks:', data.proposedTasks);

      const serverCreated = !!(data.tasksCreated ?? data.tasks_created);
      if (!data.meeting) {
        console.warn('No meeting object in response; server may have created tasks already:', serverCreated);
      }

      if (!Array.isArray(data.proposedTasks)) {
        console.warn('No proposedTasks array in response, defaulting to empty array');
      }

      return {
        success: true,
        meeting: data.meeting,
        proposedTasks: Array.isArray(data.proposedTasks) ? data.proposedTasks : [],
        jobs: Array.isArray(data.jobs) ? data.jobs : [],
        people: Array.isArray(data.people) ? data.people : [],
        serverCreated
      };
    } catch (error) {
      console.error('Error processing meeting:', error);
      throw error;
    } finally {
      setIsProcessing(false);
    }
  }, []);

  const createTasks = useCallback(async (meetingId: string, tasks: any[]) => {
    setIsProcessing(true);
    
    try {
      console.log('Creating tasks for meeting:', meetingId, 'with', tasks?.length || 0, 'tasks');
      console.log('Tasks payload:', tasks);
      
      const { data, error } = await supabase.functions.invoke('create-meeting-tasks', {
        body: {
          meetingId,
          tasks
        }
      });

      console.log('Create tasks response:', { data, error });

      if (error) {
        console.error('Task creation error:', error);
        throw new Error(error.message || 'Failed to create tasks');
      }

      if (!data) {
        throw new Error('No response from task creation');
      }

      console.log('Tasks created:', data);

      return {
        success: true,
        tasksCreated: data.tasksCreated || 0
      };
    } catch (error) {
      console.error('Error creating tasks:', error);
      throw error;
    } finally {
      setIsProcessing(false);
    }
  }, []);

  return {
    isRecording,
    isProcessing,
    recordingDuration,
    startRecording,
    stopRecording,
    processMeeting,
    createTasks
  };
};